
export enum Subject {
  Physics = 'Physics',
  Chemistry = 'Chemistry',
  Maths = 'Maths',
  GCSEMaths = 'GCSE Maths',
  FurtherMaths = 'Further Maths',
  Biology = 'Biology',
  GCSEBiology = 'GCSE Biology',
  GCSEPhysics = 'GCSE Physics',
  GCSEChemistry = 'GCSE Chemistry',
  GCSEHistory = 'GCSE History',
  Economics = 'Economics',
  General = 'General'
}

export enum MessageRole {
  User = 'user',
  Model = 'model'
}

export interface Attachment {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  kind: 'image' | 'text' | 'file';
  dataUrl?: string;
  textPreview?: string;
}

export interface Message {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
  isThinking?: boolean;
  groundingUrls?: string[];
  audioData?: string; // Base64 audio for TTS
  attachments?: Attachment[];
}

export interface PaperMetadata {
  subject?: string;
  year?: string;
  season?: string;
  board?: string;
  paper?: string;
  obtainedMarks?: number;
  totalMarks?: number;
  generated?: boolean;
}

export interface PaperPageMeta {
  pageNumber: number;
  width: number;
  height: number;
  dataUrl?: string;
}

export interface PaperChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date | number | string;
}

export interface PaperQuestion {
  id: string;
  content: string;
  isCompleted?: boolean;
  whiteboardData?: any[]; // Excalidraw elements
  maxMarks?: number;
  obtainedMarks?: number;
  firstAttemptMarks?: number;
  attemptsCount?: number;
}

export interface GeneratedPaperPart {
  label: string;
  text: string;
  marks: number;
  answerLines?: number;
  diagramLines?: number;
}

export interface GeneratedPaperQuestion {
  number: string;
  text: string;
  marks: number;
  answerLines?: number;
  diagramLines?: number;
  parts?: GeneratedPaperPart[];
}

export interface GeneratedPaperSection {
  title: string;
  questions: GeneratedPaperQuestion[];
}

export interface GeneratedMarkSchemePart {
  label: string;
  marks: number;
  points: string[];
}

export interface GeneratedMarkSchemeItem {
  number: string;
  marks: number;
  points?: string[];
  parts?: GeneratedMarkSchemePart[];
}

export interface GeneratedPaper {
  title: string;
  board: string;
  subject: string;
  level: string;
  totalMarks: number;
  instructions?: string[];
  sections: GeneratedPaperSection[];
  markScheme?: GeneratedMarkSchemeItem[];
}

export interface Session {
  id: string;
  userId: string;
  subject: Subject;
  title: string;
  messages: Message[];
  startTime: number;
  lastUpdated: number;
  mode?: AppMode; // Track which tool created this session
  topicHierarchy?: string[]; // Track topic path
  paperMetadata?: PaperMetadata; // Metadata for AI Marker sessions
  paperPages?: PaperPageMeta[]; // Paper pages (metadata only for Paper Guide)
  markSchemePages?: PaperPageMeta[]; // Mark scheme pages (metadata only)
  chatHistory?: PaperChatMessage[]; // Paper Guide chat history
  currentPageIndex?: number; // Paper Guide current page
  whiteboardData?: any; // Excalidraw elements for persistence (General use)
  whiteboardEngine?: 'excalidraw' | 'konva'; // Which whiteboard engine to use (default: excalidraw)
  flashcards?: { front: string; back: string }[]; // Flashcard data
  questionsList?: PaperQuestion[] | GeneratedPaper; // For Paper Guide - Detailed list or generated paper
  currentQuestionIndex?: number; // For Paper Guide
  markSchemeContent?: string; // Transcribed mark scheme text for Paper Guide
  currentQuestion?: string; // Current question text for Interactive Whiteboard
  currentFeedback?: string; // Last AI feedback for Interactive Whiteboard
  
  // Live Collaboration
  isLive?: boolean;
  participants?: string[]; // IDs of users invited to this session

  // AI-Generated Text Notes
  isAiGenerated?: boolean; // True if this note was generated by AI (shows text editor instead of whiteboard)
  textContent?: string; // Markdown content for AI-generated notes

  // Client-only cache metadata (never persisted)
  __fields?: string[];
  __summary?: boolean;
}

export type AIProvider = 'gemini' | 'grok' | 'openrouter' | 'openrouter-thinking' | 'alibaba' | 'groq';

export type SubscriptionStatus = 'free' | 'active' | 'cancelled' | 'past_due' | 'trialing';
export type SubscriptionPlan = 'monthly' | 'yearly';

export type EducationLevel = 'GCSE' | 'A-Level' | 'University' | 'Independent';

export interface User {
  id: string;
  email: string;
  name: string;
  phoneNumber?: string;
  level?: EducationLevel;
  subjects?: Subject[];
  examBoards?: Record<string, string>; // e.g. { Physics: 'AQA' }
  theme?: 'light' | 'dark'; // UI theme preference
  aiProvider?: AIProvider; // AI model provider preference (default: gemini)
  subscriptionStatus?: SubscriptionStatus;
  subscriptionPlan?: SubscriptionPlan;
  subscriptionUpdatedAt?: string;
}

export interface SpecData {
  subject: Subject;
  content: string;
}

export enum AppMode {
  Landing = 'landing',
  Dashboard = 'dashboard',
  Chat = 'chat',
  Voice = 'voice', // Deprecated but kept for enum stability if needed, though removed from UI
  Video = 'video',
  Auth = 'auth',
  ActiveRecall = 'active_recall',
  Solver = 'solver',
  AiMarker = 'ai_marker',
  PaperGuide = 'paper_guide',
  InteractiveWhiteboard = 'interactive_whiteboard',
  QuickLearn = 'quick_learn',
  Flashcards = 'flashcards',
  Roadmap = 'roadmap',
  Notes = 'notes',
  Focus = 'focus',
  Settings = 'settings',
  Admin = 'admin',
  MCQ = 'mcq'
}

// Progress Tracking Types
export interface TopicStats {
  obtained: number;
  total: number;
  lastAttempted?: number; // Timestamp of last activity
  subtopics: Record<string, TopicStats>;
}

export interface SubjectProgress {
  board: string;
  topics: Record<string, TopicStats>;
}

export type UserProgress = Record<string, SubjectProgress>; // Keyed by Subject enum string

export interface MarkEvent {
  id: string;
  timestamp: number;
  subject: Subject;
  marksAttempted: number;
  marksObtained: number;
}

export type ErrorTag =
  | 'Conceptual gap'
  | 'Procedural slip'
  | 'Calculation error'
  | 'Misread question'
  | 'Recall gap'
  | 'Graph or diagram error'
  | 'Units or notation error'
  | 'Reasoning gap';

export interface WeaknessEvent {
  id: string;
  userId: string;
  timestamp: number;
  subject: Subject;
  mode: AppMode;
  marksAttempted: number;
  marksObtained: number;
  topicHierarchy?: string[];
  concept?: string;
  questionType?: string;
  difficulty?: string;
  errorTags?: ErrorTag[];
  missedConcepts?: string[];
  hintsUsed?: number;
  timeSpentSec?: number;
  attempts?: number;
  sourceSessionId?: string;
}

export interface WeaknessConceptInsight {
  id: string;
  subject: Subject;
  path: string[];
  concept: string;
  mastery: number;
  trend: number;
  attempts: number;
  lastSeen: number;
  errorTags: ErrorTag[];
  missedConcepts: string[];
  nextAction: string;
}

export interface WeaknessInsights {
  totalAttempts: number;
  weakConcepts: WeaknessConceptInsight[];
  improvingConcepts: WeaknessConceptInsight[];
  topErrorTags: { tag: ErrorTag; count: number }[];
}

export interface FocusTarget {
  subject: Subject;
  topicHierarchy: string[];
  concept: string;
  errorTags?: ErrorTag[];
  missedConcepts?: string[];
}

// Linking Types
export interface LinkRequest {
  id: string;
  fromUserId: string;
  fromUserName: string;
  toEmail: string;
  subject: Subject | 'All';
  status: 'pending' | 'accepted' | 'declined';
  timestamp: number;
}

export interface LinkedConnection {
    userId: string;
    userName: string;
    subject: Subject | 'All';
}

export interface AdminMetrics {
  totalUsers: number;
  totalSessions: number;
  totalMarkEvents: number;
  totalMarksAttempted: number;
  totalMarksObtained: number;
  totalSessionHours: number;
  activeUsers30d: number;
  marksAttempted30d: number;
  marksObtained30d: number;
  userEmails: string[];
  updatedAt: string;
}
